import { CoreError } from './errors'

export interface SessionContext {
  sessionId: string
  clinicId: string
  revision: number
}

export class SessionContextFence {
  private current: SessionContext | undefined
  private blocked = false
  private epoch = 0
  private readonly listeners = new Set<() => void>()

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  capture(): number { return this.epoch }

  readonly isBlocked = (): boolean => this.blocked

  startLogin(): void {
    this.current = undefined
    this.blocked = false
    this.epoch++
    for (const listener of this.listeners) listener()
  }

  install(context: SessionContext, allowTransition = true): void {
    if (!context.sessionId || !Number.isSafeInteger(context.revision) || context.revision < 1) {
      throw new CoreError('malformed', 'Missing session context')
    }
    const changed = !this.current || this.current.sessionId !== context.sessionId ||
      this.current.clinicId !== context.clinicId || this.current.revision !== context.revision
    if (this.current && !allowTransition && (changed || this.blocked)) {
      // A passive refresh cannot install a fenced context. Re-fencing here
      // would also discard the successful response of our own pending switch.
      if (!this.blocked) this.invalidate()
      throw contextChanged()
    }
    this.current = { ...context }
    this.blocked = false
    if (changed) this.epoch++
    for (const listener of this.listeners) listener()
  }

  invalidate(): void {
    this.blocked = true
    this.epoch++
    for (const listener of this.listeners) listener()
  }

  assertCurrent(epoch: number): void {
    if (this.blocked || epoch !== this.epoch) throw contextChanged()
  }

  assertUnchanged(epoch: number): void {
    if (epoch !== this.epoch) throw contextChanged()
  }

  headers(required = true): Record<string, string> {
    if (required && (this.blocked || !this.current)) throw contextChanged()
    return this.current ? {
      'X-Terysling-Session-Id': this.current.sessionId,
      'X-Terysling-Clinic-Id': this.current.clinicId,
      'X-Terysling-Context-Revision': String(this.current.revision),
    } : {}
  }

  nativeURLIfCurrent(path: string): string | undefined {
    if (this.blocked || !this.current) return undefined
    return this.nativeURL(path)
  }

  nativeURL(path: string): string {
    this.headers()
    const url = new URL(path, window.location.origin)
    if (url.origin !== window.location.origin) throw contextChanged()
    url.searchParams.set('contextSessionId', this.current!.sessionId)
    url.searchParams.set('contextClinicId', this.current!.clinicId)
    url.searchParams.set('contextRevision', String(this.current!.revision))
    return url.pathname + url.search + url.hash
  }
}

function contextChanged(): CoreError {
  return new CoreError('conflict', 'Organization context changed', 409, 'session_context_changed')
}

export function isAccountContextPath(path: string, method = 'GET'): boolean {
  const pathname = path.split('?')[0] ?? ''
  if (method === 'GET' && pathname === '/api/v1/account/design-connections') return true
  if (method === 'DELETE' && /^\/api\/v1\/account\/design-connections\/[0-9a-f-]+$/i.test(pathname)) return true
  if (method === 'POST' && ['/api/v1/account/design-authorizations/preview', '/api/v1/account/design-authorizations'].includes(pathname)) return true
  if (method === 'GET' && pathname === '/api/v1/account/external-connections') return true
  if (method === 'DELETE' && /^\/api\/v1\/account\/external-connections\/[0-9a-f-]+$/i.test(pathname)) return true
  if (pathname === '/api/v1/admin/organization-quotas' || /^\/api\/v1\/admin\/organization-quotas\/[0-9a-f-]+$/i.test(pathname)) return true
  return [
    '/api/v1/auth/login', '/api/v1/auth/logout', '/api/v1/auth/telegram-mini-app',
    '/api/v1/auth/session', '/api/v1/auth/me', '/api/v1/app-context',
    '/api/v1/auth/password-policy', '/api/v1/account/password',
    '/api/v1/account/password-enrollment',
    '/api/v1/account/emails',
    '/api/v1/account/email-challenge',
    '/api/v1/staff-invitations/preview', '/api/v1/staff-invitations/accept',
    '/api/v1/account/language', '/api/v1/account/ui-settings',
  ].includes(pathname)
}

export const browserSessionContext = new SessionContextFence()
const contextChannel = typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined'
  ? undefined : new window.BroadcastChannel('terysling-session-context')
if (contextChannel) contextChannel.onmessage = () => browserSessionContext.invalidate()

export function notifySessionContextChange(): void {
  contextChannel?.postMessage('changed')
}
